import React, { useState, useEffect, useMemo } from 'react';
import { tenantsAPI, documentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { CheckCircle2, Circle, ClipboardList, Upload, FileText } from 'lucide-react';

const RequiredDocumentsChecklist = ({ user, onUploadFor, refreshKey = 0 }) => {
  const [types, setTypes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.tenant_id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      tenantsAPI.getMyDocumentTypes(),
      documentsAPI.list({ tenant_id: user.tenant_id, module_id: 'document_submission' }),
    ])
      .then(([typesRes, docsRes]) => {
        if (cancelled) return;
        setTypes(typesRes.data || []);
        setDocs(docsRes.data || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user?.tenant_id, refreshKey]);

  const { completed, requiredTotal, percent } = useMemo(() => {
    const uploadedKeys = new Set(docs.map((d) => d.document_type));
    const reqTypes = types.filter((t) => t.required);
    const done = reqTypes.filter((t) => uploadedKeys.has(t.key)).length;
    const total = reqTypes.length;
    return {
      completed: done,
      requiredTotal: total,
      percent: total === 0 ? 100 : Math.round((done / total) * 100),
    };
  }, [types, docs]);

  if (!user?.tenant_id) return null;
  if (loading) return null;
  if (types.length === 0) return null;

  const uploadedByKey = docs.reduce((acc, d) => {
    acc[d.document_type] = acc[d.document_type] || [];
    acc[d.document_type].push(d);
    return acc;
  }, {});

  return (
    <Card data-testid="required-docs-checklist">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-indigo-600" />
            <CardTitle>Required Documents</CardTitle>
          </div>
          <Badge
            variant="outline"
            className={
              percent === 100
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }
            data-testid="checklist-progress-badge"
          >
            {completed} / {requiredTotal} required uploaded
          </Badge>
        </div>
        <CardDescription>
          Your organization requires the following documents.
        </CardDescription>
        <div className="pt-2">
          <Progress value={percent} className="h-2" data-testid="checklist-progress" />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {types.map((t) => {
          const uploads = uploadedByKey[t.key] || [];
          const done = uploads.length > 0;
          return (
            <div
              key={t.key}
              className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
              data-testid={`checklist-item-${t.key}`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {done ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                ) : (
                  <Circle className="h-5 w-5 text-slate-300 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-slate-900">{t.label}</p>
                    {t.required ? (
                      <Badge className="bg-red-50 text-red-700 border border-red-100 text-[10px] uppercase">
                        Required
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-50 text-slate-600 border border-slate-200 text-[10px] uppercase">
                        Optional
                      </Badge>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {(t.accepted_file_types || []).map((ft) => (
                      <span
                        key={ft}
                        className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-slate-100 text-slate-600"
                      >
                        .{ft}
                      </span>
                    ))}
                  </div>
                  {done && (
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {uploads.length} file{uploads.length > 1 ? 's' : ''} uploaded
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant={done ? 'outline' : 'default'}
                onClick={() => onUploadFor && onUploadFor(t)}
                data-testid={`checklist-upload-${t.key}`}
              >
                <Upload className="mr-2 h-3.5 w-3.5" />
                {done ? 'Upload more' : 'Upload'}
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default RequiredDocumentsChecklist;
